import { Plus } from 'lucide-react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from '@/_shared/components/ui/select';
import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { formatDateTimeTitle, timeAgo } from '@/_shared/lib/format';

import { tokenScopesFor } from './constants';
import type { NewlyCreatedAccessToken, TokenScope } from './types';

interface CreateTokenDialogProps {
  createOpen: boolean;
  onOpenChange: (open: boolean) => void;
  newlyCreatedToken: NewlyCreatedAccessToken | null;
  tokenName: string;
  onTokenNameChange: (value: string) => void;
  tokenScope: TokenScope;
  onTokenScopeChange: (value: TokenScope) => void;
  /** Owners and admins only: the "Manage the Context Hub" switch is not rendered for members. */
  isAdmin: boolean;
  manageContext: boolean;
  onManageContextChange: (value: boolean) => void;
  onCreateToken: () => void;
  createPending: boolean;
}

export function CreateTokenDialog({
  createOpen,
  onOpenChange,
  newlyCreatedToken,
  tokenName,
  onTokenNameChange,
  tokenScope,
  onTokenScopeChange,
  isAdmin,
  manageContext,
  onManageContextChange,
  onCreateToken,
  createPending,
}: CreateTokenDialogProps): React.ReactElement {
  const { isMcpAudienceToolsEnabled } = useWorkspace();
  const scopes = tokenScopesFor(isMcpAudienceToolsEnabled);
  return (
    <Dialog open={createOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="secondary" Icon={<Plus />} label="Create token" />
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {newlyCreatedToken ? (
          <>
            <DialogHeader>
              <DialogTitle>Access token created</DialogTitle>
              <DialogDescription>Copy your token now. You won&apos;t be able to see it again.</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <Input
                type="password"
                size="lg"
                className="flex-1 font-mono"
                readOnly
                value={newlyCreatedToken.token}
                aria-label="New access token"
              />
              <CopyButton size="lg" text={newlyCreatedToken.token} copyLabel="Token" copiedFeedback />
            </div>
            <p className="text-xs text-muted-foreground" title={formatDateTimeTitle(newlyCreatedToken.expiresAt)}>
              Expires {timeAgo(newlyCreatedToken.expiresAt)}
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="primary" label="Done" />
              </DialogClose>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create access token</DialogTitle>
              <DialogDescription>
                Generate a token for programmatic access to your workspace MCP tools.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label htmlFor="token-name" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Name
                </label>
                <Input
                  id="token-name"
                  type="text"
                  value={tokenName}
                  onChange={(e) => onTokenNameChange(e.target.value)}
                  placeholder="e.g. CI pipeline, Cursor"
                />
              </div>
              <div>
                <label htmlFor="token-scope" className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Permissions
                </label>
                <Select value={tokenScope} onValueChange={onTokenScopeChange}>
                  <SelectTrigger id="token-scope" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className="w-(--radix-select-trigger-width)">
                    {scopes.map((scope) => (
                      <SelectItem key={scope.value} value={scope.value}>
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <SelectItemText>{scope.title}</SelectItemText>
                          <span className="text-xs text-muted-foreground">{scope.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin ? (
                <div className="flex items-start gap-3">
                  <Switch
                    id="token-manage-context"
                    className="mt-0.5"
                    checked={manageContext}
                    onCheckedChange={onManageContextChange}
                    aria-describedby="token-manage-context-help"
                  />
                  <div className="min-w-0">
                    <Label htmlFor="token-manage-context">Manage the Context Hub</Label>
                    <p id="token-manage-context-help" className="mt-0.5 text-xs text-muted-foreground">
                      Create, edit and delete notes, memories and skills. Owners and admins only.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary" label="Cancel" />
              </DialogClose>
              <Button
                type="button"
                variant="primary"
                label={createPending ? 'Creating…' : 'Create'}
                disabled={!tokenName.trim() || createPending}
                onClick={onCreateToken}
              />
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
